import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import ManagerLayout from "./ManagerLayout";
import { toast, Toaster } from "sonner";
import { Banknote, Smartphone, CheckCircle2, X } from "lucide-react";
import { ddmmyyyy, time12h } from "../../lib/dates";
import ExternalCompleteModal from "../../components/ExternalCompleteModal";

const PAY_TABS = [
    { key: "all", label: "All Payments" },
    { key: "partial", label: "Pending Balance" },
    { key: "paid", label: "Paid in Full" },
];

export default function ManagerBookings() {
    const [bookings, setBookings] = useState([]);
    const [centres, setCentres] = useState([]);
    const [centreFilter, setCentreFilter] = useState("all");
    const [payFilter, setPayFilter] = useState("partial");
    const [collectId, setCollectId] = useState(null);
    const [collectMethod, setCollectMethod] = useState("cash");
    const [submitting, setSubmitting] = useState(false);
    const [externalCompleteId, setExternalCompleteId] = useState(null);  // Iter 35

    const fetchAll = async () => {
        const [b, c] = await Promise.all([
            api.get("/manager/bookings"),
            api.get("/manager/centres"),
        ]);
        setBookings(b.data);
        setCentres(c.data);
    };
    useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const filtered = useMemo(() => bookings
        .filter(b => centreFilter === "all" ? true : b.centre_id === centreFilter)
        .filter(b => payFilter === "all" ? true : b.payment_status === payFilter), [bookings, centreFilter, payFilter]);

    const counts = useMemo(() => ({
        pending: bookings.filter(b => b.payment_status === "partial" && b.status !== "cancelled").length,
        balance: bookings.filter(b => b.status !== "cancelled").reduce((s, b) => s + (b.balance_due || 0), 0),
    }), [bookings]);

    const collectingBooking = bookings.find(b => b.id === collectId);

    const submitCollect = async () => {
        if (!collectingBooking) return;
        setSubmitting(true);
        try {
            const { data } = await api.post(`/bookings/${collectingBooking.id}/collect-balance`, { method: collectMethod });
            toast.success(`Collected ₹${collectingBooking.balance_due} via ${data.method.toUpperCase()}`);
            setCollectId(null);
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally {
            setSubmitting(false);
        }
    };

    const cancelBooking = async (b) => {
        if (!confirm(`Cancel booking ${b.booking_code} for ${b.user_name}?\n\nRefund policy:\n• >24h to slot: 100% auto-refund\n• 24h–3h: 50% — admin approval required\n• <3h: no refund (still admin reviewed)`)) return;
        try {
            const { data } = await api.post(`/bookings/${b.id}/cancel`);
            if (data.auto_refund) {
                toast.success(`Cancelled. ₹${data.refund} refund initiated (${data.tier_pct}%).`);
            } else if (data.tier_pct === 0) {
                toast.success("Cancellation submitted — no refund. Awaiting admin confirmation.");
            } else {
                toast.success(`Cancellation submitted. Suggested ₹${data.suggested_refund} (${data.tier_pct}%). Awaiting admin approval.`);
            }
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Cancel failed");
        }
    };

    return (
        <ManagerLayout>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="p-4 sm:p-8">
                <h1 className="font-display text-3xl font-bold uppercase text-white">Bookings & Collections</h1>
                <p className="text-slate-400 text-sm mt-1">Manage today&apos;s bookings and collect pending balances at venue.</p>

                {/* KPIs */}
                <div className="mt-6 grid grid-cols-2 gap-3">
                    <div className="bg-slate-900 border border-amber-700/40 rounded p-4" data-testid="kpi-pending">
                        <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400">Pending Balance Bookings</div>
                        <div className="font-display text-3xl font-bold text-amber-400 mt-1">{counts.pending}</div>
                    </div>
                    <div className="bg-slate-900 border border-amber-700/40 rounded p-4" data-testid="kpi-balance">
                        <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400">Balance Due (Total)</div>
                        <div className="font-display text-3xl font-bold text-amber-400 mt-1">₹{counts.balance}</div>
                    </div>
                </div>

                {/* Filters */}
                <div className="mt-6 space-y-3">
                    {centres.length > 1 && (
                        <div>
                            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-2">Centre</div>
                            <div className="flex gap-2 flex-wrap">
                                <button onClick={() => setCentreFilter("all")} data-testid="centre-filter-all"
                                    className={`px-3 h-9 rounded-full text-xs font-bold uppercase tracking-widest border ${
                                        centreFilter === "all" ? "bg-[#00FF66] text-black border-[#00FF66]" : "bg-slate-900 text-slate-300 border-slate-800"
                                    }`}>All</button>
                                {centres.map(c => (
                                    <button key={c.id} onClick={() => setCentreFilter(c.id)} data-testid={`centre-filter-${c.id}`}
                                        className={`px-3 h-9 rounded-full text-xs font-bold uppercase tracking-widest border ${
                                            centreFilter === c.id ? "bg-[#00FF66] text-black border-[#00FF66]" : "bg-slate-900 text-slate-300 border-slate-800"
                                        }`}>{c.name.replace("Drive or Kick ", "")}</button>
                                ))}
                            </div>
                        </div>
                    )}
                    <div>
                        <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-2">Payment</div>
                        <div className="flex gap-2 flex-wrap">
                            {PAY_TABS.map(t => (
                                <button key={t.key} onClick={() => setPayFilter(t.key)} data-testid={`pay-filter-${t.key}`}
                                    className={`px-3 h-9 rounded-full text-xs font-bold uppercase tracking-widest border ${
                                        payFilter === t.key ? "bg-[#00FF66]/20 text-[#00FF66] border-[#00FF66]" : "bg-slate-900 text-slate-300 border-slate-800"
                                    }`}>{t.label}</button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* List */}
                <div className="mt-6 bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-slate-400 text-xs uppercase tracking-widest border-b border-slate-800 bg-slate-950/50">
                                    <th className="text-left py-3 px-4">Code</th>
                                    <th className="text-left py-3 px-4">Customer</th>
                                    <th className="text-left py-3 px-4">Centre · Sport</th>
                                    <th className="text-left py-3 px-4">Date · Time</th>
                                    <th className="text-left py-3 px-4">Source</th>
                                    <th className="text-right py-3 px-4">Total</th>
                                    <th className="text-right py-3 px-4">Paid</th>
                                    <th className="text-right py-3 px-4">Balance</th>
                                    <th className="text-left py-3 px-4">Status</th>
                                    <th className="text-left py-3 px-4">Payment Status</th>
                                    <th className="text-right py-3 px-4">Action</th>
                                </tr>
                            </thead>
                            <tbody className="text-slate-300">
                                {filtered.length === 0 && (
                                    <tr><td colSpan={11} className="py-8 text-center text-slate-500">No bookings match the filters.</td></tr>
                                )}
                                {filtered.map(b => (
                                    <tr key={b.id} className="border-b border-slate-900" data-testid={`mgr-booking-${b.id}`}>
                                        <td className="py-3 px-4 font-mono text-xs text-[#00FF66]">{b.booking_code}</td>
                                        <td className="py-3 px-4">
                                            <div className="text-white">{b.user_name}</div>
                                            <div className="text-xs text-slate-500">{b.user_phone || "—"}</div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <div>{b.centre_name}</div>
                                            <div className="text-xs text-slate-500">{b.sport}</div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <div>{ddmmyyyy(b.booking_date)}</div>
                                            <div className="text-xs text-slate-500">{time12h(b.start_time)} – {time12h(b.end_time)}</div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <span data-testid={`mgr-source-${b.id}`}
                                                className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${
                                                    b.source === "offline"
                                                        ? "bg-pink-950 text-pink-400 border-pink-900"
                                                        : "bg-cyan-950 text-cyan-400 border-cyan-900"
                                                }`}>{b.source || "online"}</span>
                                            {b.source === "offline" && b.booked_by_name && (
                                                <div className="mt-1 text-[10px] text-slate-500 uppercase tracking-widest">
                                                    by <span className="text-pink-400 font-bold">{b.booked_by_name?.split(" ")[0]}</span>
                                                    {b.booked_by_role === "manager" && <span className="ml-1 text-pink-400">(mgr)</span>}
                                                </div>
                                            )}
                                            {b.external_source && (
                                                <div className="mt-1">
                                                    <span data-testid={`external-source-badge-${b.id}`}
                                                        className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-300 text-[10px] font-bold uppercase tracking-widest">
                                                        via {b.external_source}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-right text-white font-bold" data-testid={`mgr-total-${b.id}`}>₹{b.total}</td>
                                        <td className="py-3 px-4 text-right text-[#00FF66] font-bold">₹{b.paid_amount}</td>
                                        <td className={`py-3 px-4 text-right font-bold ${(b.balance_due > 0 || (b.amount_to_collect || 0) > 0) ? "text-amber-400" : "text-slate-500"}`} data-testid={`mgr-balance-${b.id}`}>
                                            {(() => {
                                                // Iter 34 — balance visibility for ALL bookings, including 'Booked via Other App'.
                                                // External bookings carry their owed amount in `amount_to_collect`, not `balance_due`.
                                                const external = (b.amount_to_collect || 0) > 0;
                                                const amt = external ? b.amount_to_collect : b.balance_due;
                                                if (amt > 0) {
                                                    return (
                                                        <>
                                                            ₹{amt}
                                                            {external && <div className="text-[10px] text-amber-300/70 font-normal uppercase tracking-widest mt-0.5">on-site</div>}
                                                            {!external && (b.discount || 0) > 0 && (
                                                                <div className="text-[10px] text-slate-500 font-normal uppercase tracking-widest mt-0.5">after ₹{b.discount} off</div>
                                                            )}
                                                        </>
                                                    );
                                                }
                                                return "—";
                                            })()}
                                        </td>
                                        <td className="py-3 px-4">
                                            <StatusBadge status={b.status} />
                                        </td>
                                        <td className="py-3 px-4">
                                            <PayBadge status={b.payment_status} />
                                            <PaymentLineage b={b} />
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <div className="inline-flex items-center gap-2">
                                                {/* Iter 35 — Complete / Collect Remaining for "Booked via Other App" */}
                                                {(b.external_source || (b.amount_to_collect || 0) > 0) && b.status !== "cancelled" && (b.payment_status !== "paid") && (() => {
                                                    const rem = Math.max(0, (b.amount_to_collect || 0) - (b.paid_amount || 0));
                                                    const first = (b.collections?.length || 0) === 0;
                                                    return (
                                                        <button onClick={() => setExternalCompleteId(b.id)}
                                                            data-testid={`external-complete-btn-${b.id}`}
                                                            className="inline-flex items-center gap-1 px-3 h-8 text-[10px] font-bold uppercase tracking-widest rounded border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 whitespace-nowrap">
                                                            {first ? "Complete" : `Collect Remaining ₹${rem}`}
                                                        </button>
                                                    );
                                                })()}
                                                {b.balance_due > 0 && b.status !== "cancelled" && (
                                                    <button onClick={() => { setCollectId(b.id); setCollectMethod("cash"); }}
                                                        data-testid={`collect-btn-${b.id}`}
                                                        className="inline-flex items-center gap-1 px-3 h-8 text-[10px] font-bold uppercase tracking-widest rounded border border-[#00FF66]/40 text-[#00FF66] hover:bg-[#00FF66]/10 whitespace-nowrap">
                                                        Collect ₹{b.balance_due}
                                                    </button>
                                                )}
                                                {b.status !== "cancelled" && (
                                                    <button onClick={() => cancelBooking(b)}
                                                        data-testid={`cancel-btn-${b.id}`}
                                                        title="Cancel this booking"
                                                        className="inline-flex items-center gap-1 px-3 h-8 text-[10px] font-bold uppercase tracking-widest rounded border border-red-500/40 text-red-400 hover:bg-red-500/10 whitespace-nowrap">
                                                        Cancel
                                                    </button>
                                                )}
                                                {b.status === "cancelled" && (
                                                    <span className="text-red-500/60 text-[10px] font-bold uppercase tracking-widest">Cancelled</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Collect Modal */}
                {collectingBooking && (
                    <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-lg p-6 relative" data-testid="collect-modal">
                            <button onClick={() => setCollectId(null)} className="absolute top-3 right-3 text-slate-500 hover:text-white">
                                <X className="w-5 h-5" />
                            </button>
                            <h3 className="font-display text-2xl font-bold uppercase text-white">Collect Balance</h3>
                            <div className="mt-4 bg-slate-950 border border-slate-800 rounded p-4 text-sm space-y-1">
                                <div className="flex justify-between"><span className="text-slate-400">Customer</span><span className="text-white">{collectingBooking.user_name}</span></div>
                                <div className="flex justify-between"><span className="text-slate-400">Booking</span><span className="text-[#00FF66] font-mono">{collectingBooking.booking_code}</span></div>
                                <div className="flex justify-between"><span className="text-slate-400">Centre · Sport</span><span className="text-white">{collectingBooking.centre_name} · {collectingBooking.sport}</span></div>
                                <div className="flex justify-between"><span className="text-slate-400">Date · Time</span><span className="text-white">{collectingBooking.booking_date} {time12h(collectingBooking.start_time)}</span></div>
                                <div className="border-t border-slate-800 my-2" />
                                <div className="flex justify-between font-display text-2xl"><span className="text-slate-400 text-sm self-end">Amount</span><span className="text-[#00FF66] font-bold">₹{collectingBooking.balance_due}</span></div>
                            </div>

                            <div className="mt-5">
                                <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mb-3">Method of payment</div>
                                <div className="grid grid-cols-2 gap-3">
                                    {[
                                        { v: "cash", icon: Banknote, label: "Cash" },
                                        { v: "upi", icon: Smartphone, label: "UPI" },
                                    ].map(opt => (
                                        <button key={opt.v} onClick={() => setCollectMethod(opt.v)}
                                            data-testid={`method-${opt.v}`}
                                            className={`p-4 rounded border transition flex flex-col items-center gap-2 ${
                                                collectMethod === opt.v ? "bg-[#00FF66]/10 border-[#00FF66]" : "bg-slate-950 border-slate-700 text-slate-300"
                                            }`}>
                                            <opt.icon className={`w-6 h-6 ${collectMethod === opt.v ? "text-[#00FF66]" : "text-slate-400"}`} />
                                            <span className={`font-bold uppercase tracking-widest text-xs ${collectMethod === opt.v ? "text-[#00FF66]" : "text-white"}`}>{opt.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <button onClick={submitCollect} disabled={submitting}
                                data-testid="confirm-collect-btn"
                                className="w-full mt-5 h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded hover:bg-[#00cc52] disabled:opacity-50 glow-green inline-flex items-center justify-center gap-2">
                                <CheckCircle2 className="w-4 h-4" />
                                {submitting ? "Recording..." : `Confirm ${collectMethod.toUpperCase()} Payment`}
                            </button>
                        </div>
                    </div>
                )}

                {/* Iter 35 — External booking completion modal */}
                {externalCompleteId && (() => {
                    const b = bookings.find(x => x.id === externalCompleteId);
                    if (!b) return null;
                    return (
                        <ExternalCompleteModal
                            booking={b}
                            onClose={() => setExternalCompleteId(null)}
                            onSaved={async () => { setExternalCompleteId(null); await fetchAll(); }}
                        />
                    );
                })()}
            </div>
        </ManagerLayout>
    );
}

function PayBadge({ status }) {
    const map = {
        paid: { cls: "bg-[#00FF66]/15 text-[#00FF66] border-[#00FF66]/40", label: "Paid in Full" },
        partial: { cls: "bg-amber-950 text-amber-400 border-amber-700/50", label: "Partial" },
        pending: { cls: "bg-red-950 text-red-400 border-red-900", label: "Pending" },
    };
    const m = map[status] || map.pending;
    return <span className={`inline-block text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full border whitespace-nowrap ${m.cls}`}>{m.label}</span>;
}

function StatusBadge({ status }) {
    // Iter 34 — mirrors AdminBookings StatusBadge so managers see Completed too
    const map = {
        confirmed: "bg-[#00FF66]/15 text-[#00FF66] border-[#00FF66]/40",
        completed: "bg-slate-800 text-slate-400 border-slate-700",
        cancelled: "bg-red-950 text-red-400 border-red-900",
    };
    const cls = map[status] || map.confirmed;
    return (
        <span data-testid={`mgr-status-${status}`}
            className={`inline-block text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full border ${cls}`}>
            {status || "confirmed"}
        </span>
    );
}

function PaymentLineage({ b }) {
    const initialAmount = b.initial_paid_amount ?? b.paid_amount;
    const initialBy = b.source === "offline"
        ? `${b.booked_by_name?.split(" ")[0] || "Staff"}${b.booked_by_role === "manager" ? " (mgr)" : " (admin)"}`
        : "Online";
    const collectedAmount = b.balance_collected_amount;
    const collectedBy = b.balance_collected_by_name?.split(" ")[0];
    const collectedRole = b.balance_collected_by_role === "manager" ? "(mgr)" : "(admin)";
    const collectedMethod = b.balance_collected_method;
    return (
        <div className="mt-1 space-y-0.5 text-[10px] uppercase tracking-widest">
            {initialAmount > 0 && (
                <div className="text-slate-500" data-testid={`mgr-paid-initial-${b.id}`}>
                    <span className="text-[#00FF66] font-bold">₹{initialAmount}</span>
                    <span className="text-slate-600"> by </span>
                    <span className={initialBy === "Online" ? "text-cyan-400 font-bold" : "text-pink-400 font-bold"}>{initialBy}</span>
                </div>
            )}
            {collectedAmount > 0 && (
                <div className="text-slate-500" data-testid={`mgr-paid-collected-${b.id}`}>
                    <span className="text-[#00FF66] font-bold">+ ₹{collectedAmount}</span>
                    <span className="text-slate-600"> via </span>
                    <span className="text-cyan-400 font-bold">{collectedMethod}</span>
                    <span className="text-slate-600"> by </span>
                    <span className="text-pink-400 font-bold">{collectedBy} {collectedRole}</span>
                </div>
            )}
        </div>
    );
}
